import React, { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { Compass, Eye, EyeOff, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function LoginPage() {
  const { login, formatApiError } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const from = location.state?.from || "/";

  const onSubmit = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate(from, { replace: true });
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen w-screen flex items-center justify-center bg-slate-950 text-slate-100 font-body p-4 canvas-dot-grid">
      <Toaster theme="dark" position="top-right" />
      <div className="w-full max-w-md">
        <div className="flex items-center gap-3 mb-8">
          <div className="w-11 h-11 rounded-lg bg-gradient-to-br from-amber-500 to-amber-700 flex items-center justify-center gold-glow">
            <Compass className="h-6 w-6 text-slate-950" strokeWidth={2.5} />
          </div>
          <div>
            <div className="font-display text-xl font-semibold tracking-tight leading-none">Vastu Mandala</div>
            <div className="font-mono text-[10px] uppercase tracking-widest text-slate-500 mt-1">Floor Plan Sector Analyzer</div>
          </div>
        </div>

        <div className="bg-slate-900/80 backdrop-blur-md border border-slate-800 rounded-xl p-7 shadow-2xl">
          <h1 className="font-display text-2xl font-semibold mb-1">Sign in</h1>
          <p className="text-sm text-slate-400 mb-6">Continue to your saved plans and workspace.</p>

          <form onSubmit={onSubmit} className="space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Email</Label>
              <Input
                id="email" type="email" autoComplete="email" required
                data-testid="login-email-input"
                value={email} onChange={(e) => setEmail(e.target.value)}
                className="bg-slate-950 border-slate-800 text-slate-100 h-11"
                placeholder="you@example.com"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="font-mono text-[10px] uppercase tracking-widest text-slate-400">Password</Label>
              <div className="relative">
                <Input
                  id="password" type={showPw ? "text" : "password"} autoComplete="current-password" required
                  data-testid="login-password-input"
                  value={password} onChange={(e) => setPassword(e.target.value)}
                  className="bg-slate-950 border-slate-800 text-slate-100 h-11 pr-10"
                />
                <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 h-8 w-8 flex items-center justify-center">
                  {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>
            {error && (
              <div data-testid="login-error" className="text-sm text-red-400 bg-red-950/40 border border-red-900/50 rounded-lg px-3 py-2">
                {error}
              </div>
            )}
            <Button
              type="submit" disabled={loading}
              data-testid="login-submit-button"
              className="w-full h-11 bg-amber-500 hover:bg-amber-400 text-slate-950 font-semibold"
            >
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
            </Button>
          </form>

          <div className="mt-5 text-center">
            <Link data-testid="link-to-forgot" to="/forgot-password" className="text-sm text-amber-400 hover:text-amber-300 font-medium">
              Forgot password?
            </Link>
          </div>

          <div className="mt-6 pt-5 border-t border-slate-800 text-xs text-slate-500 text-center leading-relaxed">
            Access is by invitation only. Your administrator will email you a link to set up your account.
          </div>
        </div>
      </div>
    </div>
  );
}
